import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { apiBaseUrl } from 'App';
import PrimaryButton from 'components/buttons/PrimaryButton';
import TextInput from 'components/forms/basefields/TextInput';
import TextareaInput from 'components/forms/basefields/TextareaInput';

interface Branding {
    portal_logo: string | null;
    portal_nav_color: string;
    portal_background_color: string;
    portal_button_color: string;
    portal_font: string;
}

interface PortalData {
    status: 'available' | 'accepted' | 'unavailable';
    proposal_number: string | null;
    proposal_date: string | null;
    expiration_date: string | null;
    introduction_text: string | null;
    recipient_name: string | null;
    accepted_name: string | null;
    accepted_place: string | null;
    accepted_at: string | null;
    branding: Branding;
}

const FONT_STACKS: Record<string, string> = {
    arial: 'Arial, Helvetica, sans-serif',
    helvetica: 'Helvetica Neue, Helvetica, sans-serif',
    times_new_roman: '"Times New Roman", Times, serif',
    verdana: 'Verdana, Geneva, sans-serif',
    georgia: 'Georgia, serif',
    tahoma: 'Tahoma, Geneva, sans-serif',
};

const ProposalPortalPage: React.FC = () => {
    const { t } = useTranslation();
    const { token } = useParams<{ token: string }>();

    const [data, setData] = useState<PortalData | null>(null);
    const [loading, setLoading] = useState(true);
    const [unavailable, setUnavailable] = useState(false);

    // accept form state
    const [acceptedName, setAcceptedName] = useState('');
    const [acceptedPlace, setAcceptedPlace] = useState('');
    const [acceptErrors, setAcceptErrors] = useState<Record<string, string>>({});
    const [acceptSubmitting, setAcceptSubmitting] = useState(false);
    const [acceptDone, setAcceptDone] = useState(false);

    // comment form state
    const [comment, setComment] = useState('');
    const [commentErrors, setCommentErrors] = useState<Record<string, string>>({});
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [commentDone, setCommentDone] = useState(false);

    // fetch portal data on mount
    useEffect(() => {
        const loadPortalData = async () => {
            try {
                const response = await axios.get(`${apiBaseUrl}/portal/proposal/${token}/`);
                const portalData: PortalData = response.data;

                if (portalData.status === 'unavailable') {
                    setUnavailable(true);
                } else {
                    setData(portalData);
                }
            } catch {
                setUnavailable(true);
            } finally {
                setLoading(false);
            }
        };
        loadPortalData();
    }, [token]);

    const handleAccept = async (e: React.FormEvent) => {
        e.preventDefault();
        setAcceptErrors({});
        setAcceptSubmitting(true);
        try {
            await axios.post(`${apiBaseUrl}/portal/proposal/${token}/accept/`, {
                accepted_name: acceptedName,
                accepted_place: acceptedPlace,
            });
            setAcceptDone(true);
        } catch (error: any) {
            if (error.response?.data) {
                setAcceptErrors(error.response.data);
            }
        } finally {
            setAcceptSubmitting(false);
        }
    };

    const handleComment = async (e: React.FormEvent) => {
        e.preventDefault();
        setCommentErrors({});
        setCommentSubmitting(true);
        try {
            await axios.post(`${apiBaseUrl}/portal/proposal/${token}/comment/`, { comment });
            setCommentDone(true);
            setComment('');
        } catch (error: any) {
            if (error.response?.data) {
                setCommentErrors(error.response.data);
            }
        } finally {
            setCommentSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                <p>{t('proposal.portal.loading_label')}</p>
            </div>
        );
    }

    if (unavailable || !data) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', flexDirection: 'column', gap: '12px' }}>
                <h2>{t('proposal.portal.unavailable_title')}</h2>
                <p>{t('proposal.portal.unavailable_explanation')}</p>
            </div>
        );
    }

    const branding = data.branding;
    const fontFamily = FONT_STACKS[branding.portal_font] || FONT_STACKS.arial;
    const isAccepted = data.status === 'accepted' || acceptDone;

    return (
        <div style={{ minHeight: '100vh', backgroundColor: branding.portal_background_color, fontFamily }}>

            {/* top navigation bar with optional company logo */}
            <div style={{
                backgroundColor: branding.portal_nav_color,
                padding: '0 24px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
            }}>
                {branding.portal_logo ? (
                    <img src={branding.portal_logo} alt='Logo' style={{ maxHeight: '36px', maxWidth: '160px', objectFit: 'contain' }} />
                ) : (
                    <span style={{ color: '#ffffff', fontWeight: 600, fontSize: '16px' }}>
                        {data.recipient_name || t('proposal.portal.title_label')}
                    </span>
                )}
            </div>

            <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 16px' }}>

                {/* proposal header with number and expiration date */}
                <div style={{ marginBottom: '24px' }}>
                    {data.proposal_number && (
                        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 8px 0' }}>
                            {t('proposal.portal.title_label')} {data.proposal_number}
                        </h1>
                    )}
                    {data.recipient_name && (
                        <p style={{ margin: '0 0 4px 0', color: '#555' }}>{t('proposal.portal.recipient_label')} {data.recipient_name}</p>
                    )}
                    {data.expiration_date && (
                        <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>
                            {t('proposal.portal.expiration_label')} {new Date(data.expiration_date).toLocaleDateString('nl-NL')}
                        </p>
                    )}
                </div>

                {/* inline pdf viewer */}
                <iframe
                    src={`${apiBaseUrl}/portal/proposal/${token}/pdf/`}
                    style={{
                        width: '100%',
                        height: '600px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        marginBottom: '32px',
                        backgroundColor: '#fff',
                    }}
                    title={t('proposal.portal.pdf_title')}
                />

                {/* confirmation block when the proposal is already accepted */}
                {isAccepted && (
                    <div style={{ padding: '24px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '24px' }}>
                        <h3 style={{ margin: '0 0 8px 0', color: '#2a7a3b' }}>✓ {t('proposal.portal.accepted_title')}</h3>
                        {(data.accepted_name || acceptedName) && (
                            <p style={{ margin: 0, color: '#555' }}>
                                {t('proposal.portal.accepted_by_label')} <strong>{data.accepted_name || acceptedName}</strong>
                                {(data.accepted_place || acceptedPlace) && `, ${data.accepted_place || acceptedPlace}`}
                            </p>
                        )}
                    </div>
                )}

                {/* accept form — hidden after acceptance */}
                {!isAccepted && (
                    <div style={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd', padding: '24px', marginBottom: '24px' }}>
                        <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0' }}>{t('proposal.portal.accept_title')}</h2>
                        <p style={{ margin: '0 0 20px 0', color: '#555', fontSize: '14px' }}>
                            {t('proposal.portal.accept_explanation')}
                        </p>
                        <form onSubmit={handleAccept}>

                            {/* name input */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('proposal.portal.name_label')}</label>
                                <TextInput
                                    name='accepted_name'
                                    placeholder='proposal.portal.name_placeholder'
                                    value={acceptedName}
                                    onChange={setAcceptedName}
                                    errorMessage={acceptErrors.accepted_name}
                                    isInvalid={!!acceptErrors.accepted_name}
                                />
                                {acceptErrors.accepted_name && (
                                    <p style={{ color: '#c0392b', fontSize: '13px', margin: '4px 0 0 0' }}>{acceptErrors.accepted_name}</p>
                                )}
                            </div>

                            {/* place input */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('proposal.portal.place_label')}</label>
                                <TextInput
                                    name='accepted_place'
                                    placeholder='proposal.portal.place_placeholder'
                                    value={acceptedPlace}
                                    onChange={setAcceptedPlace}
                                    errorMessage={acceptErrors.accepted_place}
                                    isInvalid={!!acceptErrors.accepted_place}
                                />
                                {acceptErrors.accepted_place && (
                                    <p style={{ color: '#c0392b', fontSize: '13px', margin: '4px 0 0 0' }}>{acceptErrors.accepted_place}</p>
                                )}
                            </div>

                            {/* readonly date showing today */}
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>{t('proposal.portal.date_label')}</label>
                                <TextInput
                                    name='accept_date'
                                    placeholder=''
                                    value={new Date().toLocaleDateString('nl-NL')}
                                    onChange={() => {}}
                                    disabled={true}
                                />
                            </div>

                            <PrimaryButton
                                label='proposal.portal.accept_title'
                                type='submit'
                                loading={acceptSubmitting}
                            />
                        </form>
                    </div>
                )}

                {/* comment form — always visible unless accepted */}
                {!isAccepted && (
                    <div style={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd', padding: '24px' }}>
                        <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0' }}>{t('proposal.portal.comment_title')}</h2>

                        {commentDone ? (
                            <p style={{ color: '#2a7a3b', margin: 0 }}>{t('proposal.portal.comment_sent_message')}</p>
                        ) : (
                            <form onSubmit={handleComment}>

                                {/* comment textarea */}
                                <div style={{ marginBottom: '16px' }}>
                                    <TextareaInput
                                        name='comment'
                                        placeholder='proposal.portal.comment_placeholder'
                                        value={comment}
                                        rows={5}
                                        onChange={value => {
                                            if (typeof value === 'string') setComment(value);
                                            else setComment((value as React.ChangeEvent<HTMLTextAreaElement>).target.value);
                                        }}
                                        isInvalid={!!commentErrors.comment}
                                    />
                                    {commentErrors.comment && (
                                        <p style={{ color: '#c0392b', fontSize: '13px', margin: '4px 0 0 0' }}>{commentErrors.comment}</p>
                                    )}
                                </div>

                                <PrimaryButton
                                    label='proposal.portal.comment_submit_label'
                                    type='submit'
                                    loading={commentSubmitting}
                                />
                            </form>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProposalPortalPage;
